import { classNames } from '@superfunnel/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAgentChat } from '../../contexts/AgentChatContext';
import { ChatMessage } from '../../types/agentChat';
import { AgentIcon } from './AgentIcon';
import { AgentInterruptPrompt } from './AgentInterruptPrompt';
import { AgentMessage } from './AgentMessage';
import { UserMessage } from './UserMessage';

interface AgentChatMessagesProps {
  messages: ChatMessage[];
  isWorking: boolean;
  isLoading: boolean;
  lastUpdated: Date | null;
  className?: string;
}

export function AgentChatMessages({
  messages,
  isWorking,
  isLoading,
  lastUpdated,
  className = '',
}: AgentChatMessagesProps) {
  const { sendMessage, getAgentState, resumeFromInterrupt } = useAgentChat();
  const agentState = getAgentState();
  const [updatedRecently, setUpdatedRecently] = useState(false);

  // Ref for the messages container to control scrolling
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isUserAtBottomRef = useRef(true); // Track if user is at bottom

  // Handle retry - resend the last user message
  const handleRetry = useCallback(
    (errorType: 'error' | 'limitReached') => {
      if (errorType === 'limitReached') {
        sendMessage('Continue');
      } else {
        sendMessage('Try Again');
      }
    },
    [sendMessage],
  );

  // This is only here because when Agent streams a message and then waits for tool call,
  // it doesn't end the last message so the working indicator doesn't show.
  // We use lastUpdated to track when the agent last updated something.
  // Then if it's in the middle of streaming and hasn't updated for 500ms, we show the working indicator.
  useEffect(() => {
    let workingIndicatorTimeout: any;

    if (isWorking && !lastUpdated) {
      setUpdatedRecently(true);
    } else {
      workingIndicatorTimeout = setInterval(() => {
        if (!lastUpdated) {
          setUpdatedRecently(false);
        } else if (lastUpdated.getTime() >= Date.now() - 500) {
          setUpdatedRecently(true);
        } else {
          setUpdatedRecently(false);
        }
      }, 100);
    }

    return () => {
      clearInterval(workingIndicatorTimeout);
    };
  }, [isWorking, lastUpdated]);

  // Auto-scroll to bottom when messages change, but only if user was already at bottom
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Only auto-scroll if user was at the bottom
    if (isUserAtBottomRef.current) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, isWorking]); // Trigger on message changes or working state

  // Initial scroll to bottom when component mounts
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
      isUserAtBottomRef.current = true;
    }
  }, []); // Only run once on mount

  // Function to check if user is at the bottom of the scroll
  const checkIfAtBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const threshold = 50; // pixels from bottom to consider "at bottom"
    const isAtBottom =
      container.scrollHeight - container.clientHeight <=
      container.scrollTop + threshold;

    isUserAtBottomRef.current = isAtBottom;
  }, []);

  const lastMessage = messages[messages.length - 1];

  // Check if last message should show its inline streaming indicator
  const lastMessageShowsStreamingIndicator =
    lastMessage?.type === 'agent' &&
    lastMessage?.isStreaming &&
    !lastMessage?.content &&
    !lastMessage?.toolCalls?.length;

  return (
    <div
      ref={messagesContainerRef}
      className={classNames('flex-1 overflow-scroll px-2 py-4', className)}
      onScroll={checkIfAtBottom}
    >
      <div className="absolute -top-0.5 left-0 h-6 w-full bg-linear-to-b from-[#F7F5FF] to-transparent"></div>
      {messages.map((message: ChatMessage, index: number) => {
        // Helper function to determine if a message is agent-like (agent or error)
        const isAgentLikeMessage = (msg: ChatMessage) =>
          msg.type === 'agent' || msg.type === 'error';

        // Determine if this agent/error message should show the icon
        // Show icon if it's the first message OR if the previous message was not agent-like
        const isFirstAgentInGroup =
          isAgentLikeMessage(message) &&
          (index === 0 || !isAgentLikeMessage(messages[index - 1]));

        // Return <> for messages without contet and tools when the agent is not streaming
        if (
          message.type === 'agent' &&
          !message.content &&
          (!message.toolCalls || message.toolCalls.length === 0) &&
          !agentState.isStreaming
        ) {
          return <></>;
        }

        return (
          <div
            key={message.id}
            className={classNames(
              'group font-inter text-base-900 text-sm leading-normal font-normal tracking-tight',
              index !== 0 &&
                (!isAgentLikeMessage(message) || isFirstAgentInGroup)
                ? 'pt-4'
                : '',
            )}
          >
            {message.type === 'user' ? (
              <UserMessage message={message} />
            ) : (
              <AgentMessage
                message={message}
                isFirstInGroup={isFirstAgentInGroup}
                onRetry={message.type === 'error' ? handleRetry : undefined}
                showStreamingIndicator={
                  index === messages.length - 1 &&
                  lastMessageShowsStreamingIndicator
                }
              />
            )}
          </div>
        );
      })}
      {/* Working indicator - don't show if last message already has its own streaming indicator */}
      {isWorking &&
        (!updatedRecently || !lastMessage?.content) &&
        !lastMessageShowsStreamingIndicator && (
          <div className="flex items-start gap-2 justify-self-start pt-2 pr-2 pb-4.5">
            {/* Show icon only if last message was not agent-like (agent or error) */}
            {messages.length === 0 ||
            !(lastMessage.type === 'agent' || lastMessage.type === 'error') ? (
              <AgentIcon className="size-6 shrink-0" />
            ) : (
              // Invisible spacer to maintain alignment
              <div className="size-6 shrink-0" />
            )}

            <div className="flex items-center gap-2">
              <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-blue-500"></div>
              <span className="text-sm text-gray-600">Working...</span>
            </div>
          </div>
        )}

      {/* Interrupt prompt (shown when agent is waiting for user input) */}
      {agentState.isInterrupted && agentState.interruptData && (
        <div className="flex items-start gap-2 pt-2">
          {/* Show icon to match agent message style */}
          {messages.length === 0 ||
          !(lastMessage?.type === 'agent' || lastMessage?.type === 'error') ? (
            <AgentIcon className="size-6 shrink-0" />
          ) : (
            <div className="size-6 shrink-0" />
          )}
          <AgentInterruptPrompt
            interruptData={agentState.interruptData}
            onResume={resumeFromInterrupt}
            disabled={agentState.isLoading || agentState.isStreaming}
            className="flex-1"
          />
        </div>
      )}

      {/* Loading indicator */}
      {isLoading && (
        <div className="flex items-center justify-center py-4">
          <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-blue-500"></div>
        </div>
      )}
    </div>
  );
}
